import React, { useEffect, useState, lazy } from 'react';
import { Play } from 'lucide-react';
import { motion } from 'framer-motion';
interface VideoPlayerProps {
  title?: string;
  subtitle?: string;
  aspectRatio?: 'video' | 'square' | 'portrait';
  className?: string;
  /** YouTube video ID. When provided, the player embeds the real YouTube video on play. */
  youtubeId?: string;
  /** Loom video ID (the id from a loom.com/share/<id> URL). When provided, the player embeds the Loom video on play. */
  loomId?: string;
  /** Optional start time (in seconds) for the YouTube video. */
  startSeconds?: number;
  /** When true, the video starts playing automatically on mount (with sound — browsers may force mute until interaction). */
  autoPlay?: boolean;
}
export function VideoPlayer({
  title,
  subtitle,
  aspectRatio = 'video',
  className = '',
  youtubeId,
  loomId,
  startSeconds,
  autoPlay = false
}: VideoPlayerProps) {
  const hasVideo = Boolean(youtubeId || loomId);
  const [isPlaying, setIsPlaying] = useState(Boolean(autoPlay && hasVideo));
  // Loom recordings are often not 16:9. Fetch the real dimensions so the frame
  // matches the video exactly and there are no black letterbox bars.
  const [loomRatio, setLoomRatio] = useState<string | null>(null);
  useEffect(() => {
    if (!loomId) return;
    let cancelled = false;
    fetch(`https://www.loom.com/v1/oembed?url=https://www.loom.com/share/${loomId}`).then(res => res.ok ? res.json() : null).then(data => {
      if (!cancelled && data?.width && data?.height) {
        setLoomRatio(`${data.width} / ${data.height}`);
      }
    }).catch(() => {
      /* fall back to the default aspect ratio */
    });
    return () => {
      cancelled = true;
    };
  }, [loomId]);
  const aspectClass = {
    video: 'aspect-video',
    square: 'aspect-square',
    portrait: 'aspect-[9/16]'
  }[aspectRatio];
  // When we know the Loom video's true ratio, size the frame to it (removes black bars);
  // otherwise use the fixed Tailwind aspect class.
  const frameAspectClass = loomRatio ? '' : aspectClass;
  const frameAspectStyle = loomRatio ? {
    aspectRatio: loomRatio
  } : undefined;
  // Poster thumbnail — YouTube exposes a predictable thumbnail URL; Loom does not, so we fall back to the styled placeholder.
  const thumbnailUrl = youtubeId ? `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg` : undefined;
  let embedUrl: string | undefined;
  if (youtubeId) {
    embedUrl = `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0${startSeconds ? `&start=${startSeconds}` : ''}`;
  } else if (loomId) {
    embedUrl = `https://www.loom.com/embed/${loomId}?autoplay=1`;
  }
  // Playing state — render the real embedded video
  if (hasVideo && isPlaying) {
    return <div className={`relative rounded-xl overflow-hidden bg-black shadow-xl ${className}`} data-id="element-31">
        <div className={`relative w-full ${frameAspectClass}`} style={frameAspectStyle} data-id="element-32">
          <iframe src={embedUrl} title={title || 'Video player'} className="absolute inset-0 w-full h-full block border-0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen" allowFullScreen data-id="element-33" />
        </div>
      </div>;
  }
  return <button type="button" onClick={() => hasVideo && setIsPlaying(true)} aria-label={hasVideo ? `Play video${title ? `: ${title}` : ''}` : title || 'Video'} className={`relative group block w-full text-left rounded-xl overflow-hidden bg-white border border-slate-200 shadow-xl focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-600/40 ${hasVideo ? 'cursor-pointer' : 'cursor-default'} ${className}`} data-id="element-34">
      {/* Placeholder / Poster Background */}
      <div className={`w-full ${frameAspectClass} bg-gradient-to-br from-slate-100 to-slate-200 relative flex items-center justify-center`} style={frameAspectStyle} data-id="element-35">
        {thumbnailUrl ? <img src={thumbnailUrl} alt={title ? `${title} thumbnail` : 'Video thumbnail'} className="absolute inset-0 w-full h-full object-cover" loading="lazy" data-id="element-36" /> /* Subtle grid pattern overlay for placeholders */ : <div className="absolute inset-0 opacity-40" style={{
        backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)',
        backgroundSize: '24px 24px'
      }} data-id="element-37"></div>}

        {/* Darkening layer over real thumbnails for contrast */}
        {thumbnailUrl && <div className="absolute inset-0 bg-slate-900/20 group-hover:bg-slate-900/10 transition-colors" data-id="element-38"></div>}

        {/* Play Button */}
        <motion.span whileHover={{
        scale: 1.1
      }} whileTap={{
        scale: 0.95
      }} className="relative z-10 w-20 h-20 bg-blue-600 text-white rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(37,99,235,0.4)] group-hover:shadow-[0_0_50px_rgba(37,99,235,0.6)] transition-shadow duration-300" data-id="element-39">
          <Play className="w-8 h-8 ml-1 fill-current" data-id="element-40" />
        </motion.span>

        {/* Title Overlay (if provided) - dark so white text is readable */}
        {(title || subtitle) && <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-transparent" data-id="element-41">
            {title && <h3 className="text-xl font-bold text-white mb-1" data-id="element-42">{title}</h3>}
            {subtitle && <p className="text-sm text-slate-200" data-id="element-43">{subtitle}</p>}
          </div>}
      </div>
    </button>;
}